import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, QrCode, History, Printer, ShieldAlert, X, Search, ArrowUp, ArrowDown } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

// Wineemotion catalog for autocomplete in dispenser model input
const WINEEMOTION_MODELS = [
  "Quattro SELF 110",
  "Quattro SELF 220",
  "Otto SELF 110",
  "Otto SELF 220",
  "Otto DUAL TEMP 110",
  "Otto DUAL TEMP 220",
  "Otto DUAL TEMP EASY 110",
  "Otto DUAL TEMP EASY 220",
  "Dodici SELF 110",
  "Dodici SELF 220",
  "Sedici SELF 110",
  "Sedici SELF 220",
  "D. QUATTRO+4 8 BOTT. REFRIGERATA EU",
  "D. QUATTRO+4 8 BOTT. REFRIGERATA USA",
  "D. OTTO 8 BOTT. REFRIGERATA USA",
  "D. OTTO 8 BOTT. TEMP. AMBIENTE USA",
  "D. CINQUE 5 BOTT. REFRIGERATA USA",
];

// Top 6 chip suggestions optimised for one-tap selection on mobile
const POPULAR_MODELS = [
  "Quattro SELF 110",
  "Otto SELF 110",
  "Otto DUAL TEMP 110",
  "Otto DUAL TEMP EASY 110",
  "Dodici SELF 110",
  "Sedici SELF 110",
];

const empty = {
  serial_number: "",
  model: "",
  customer_id: "",
  install_date: "",
  location: "",
  notes: "",
  order_number: "",
  end_customer_email: "",
  end_customer_phone: "",
  status: "attivo",
};

const statusMap = {
  attivo: "bg-emerald-50 text-emerald-800 border-emerald-200",
  in_manutenzione: "bg-amber-50 text-amber-800 border-amber-200",
  dismesso: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

export default function Erogatori() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [qrFor, setQrFor] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("install_date"); // install_date | created_at | serial_number
  const [sortDir, setSortDir] = useState("desc"); // desc | asc
  const [searchParams, setSearchParams] = useSearchParams();
  const expiring = searchParams.get("expiring");

  const qrUrl = qrFor ? `${window.location.origin}/r/${qrFor.serial_number}` : "";
  const warrantyUrl = `${window.location.origin}/garanzia`;

  const printQR = () => {
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return;
    const svcSvg = document.getElementById("qr-svg-assistenza");
    const warSvg = document.getElementById("qr-svg-garanzia");
    const doc = w.document;
    doc.title = `QR ${qrFor.serial_number}`;
    const style = doc.createElement("style");
    style.textContent = "body{font-family:Inter,sans-serif;text-align:center;padding:32px;color:#111}"
      + "h1{font-size:26px;margin:8px 0;letter-spacing:-0.02em}"
      + ".sn{font-family:monospace;font-size:14px;color:#666;letter-spacing:0.1em}"
      + ".grid{display:flex;gap:24px;justify-content:center;margin-top:24px;flex-wrap:wrap}"
      + ".card{border:1px solid #ddd;padding:20px;width:280px}"
      + ".card h2{font-size:15px;margin:0 0 12px;text-transform:uppercase;letter-spacing:2px;color:#7a2036}"
      + ".card p{color:#444;font-size:12px;margin:10px 0 0;line-height:1.5}";
    doc.head.appendChild(style);
    const mk = (tag, text, cls) => { const el = doc.createElement(tag); if (cls) el.className = cls; el.textContent = text; return el; };
    doc.body.appendChild(mk("div", "WINEEMOTION", "sn"));
    doc.body.appendChild(mk("h1", qrFor.model));
    doc.body.appendChild(mk("div", `S/N ${qrFor.serial_number}`, "sn"));
    const grid = doc.createElement("div"); grid.className = "grid";
    // Support card (English)
    const c1 = doc.createElement("div"); c1.className = "card";
    c1.appendChild(mk("h2", "Technical Support"));
    if (svcSvg) c1.appendChild(doc.importNode(svcSvg, true));
    c1.appendChild(mk("p", "Scan this code to report an issue. A Wineemotion technician will take care of your request."));
    grid.appendChild(c1);
    // Warranty card (English)
    const c2 = doc.createElement("div"); c2.className = "card";
    c2.appendChild(mk("h2", "Warranty Registration"));
    if (warSvg) c2.appendChild(doc.importNode(warSvg, true));
    c2.appendChild(mk("p", "Scan to register your warranty. Enter or scan the dispenser serial number on the page. Please do this the first time you install the dispenser."));
    grid.appendChild(c2);
    doc.body.appendChild(grid);
    setTimeout(() => w.print(), 500);
  };

  const load = useCallback(async () => {
    try {
      const dispParams = expiring ? `?expiring_days=${encodeURIComponent(expiring)}` : "";
      const [d, c] = await Promise.all([api.get(`/dispensers${dispParams}`), api.get("/customers")]);
      setItems(d.data);
      setCustomers(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [expiring]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    // Client-side brand-safety: block competitor names in the model field
    const competitors = ["enomatic", "wineloft", "napa technology", "winestation", "byo"];
    const modelLc = (form.model || "").toLowerCase();
    const hit = competitors.find((b) => modelLc.includes(b));
    if (hit) {
      toast.error(`"${form.model}" sembra un competitor ("${hit}"). Inserisci un modello Wineemotion.`);
      return;
    }
    try {
      const payload = { ...form, customer_id: form.customer_id || null };
      if (editing) await api.put(`/dispensers/${editing.id}`, payload);
      else await api.post("/dispensers", payload);
      toast.success(editing ? "Erogatore aggiornato" : "Erogatore creato");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Filtro locale su seriale / cliente finale / indirizzo / modello / email / telefono
  const filteredItems = (() => {
    const q = searchQuery.trim().toLowerCase();
    let list = items;
    if (q) {
      list = items.filter((d) => {
        const serial = (d.serial_number || "").toLowerCase();
        const address = (d.order_number || "").toLowerCase();
        const model = (d.model || "").toLowerCase();
        const endCustomer = (d.location || "").toLowerCase();
        const email = (d.end_customer_email || "").toLowerCase();
        const phone = (d.end_customer_phone || "").toLowerCase();
        return serial.includes(q) || address.includes(q) || model.includes(q)
          || endCustomer.includes(q) || email.includes(q) || phone.includes(q);
      });
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      const va = a[sortBy] || "";
      const vb = b[sortBy] || "";
      if (va === vb) return 0;
      if (!va) return 1;   // empty values always at bottom
      if (!vb) return -1;
      return va > vb ? dir : -dir;
    };
    return [...list].sort(cmp);
  })();

  return (
    <div className="p-4 sm:p-8 max-w-7xl">
      <PageHeader
        title="Dispenser"
        description="Anagrafica dei dispenser Wineemotion installati. Ogni ticket è collegato al numero seriale del dispenser."
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-dispenser-btn" className="bg-brand hover:bg-brand-dark text-white rounded-sm">
                <Plus className="h-4 w-4 mr-2" /> Nuovo dispenser
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">
                  {editing ? "Modifica dispenser" : "Nuovo dispenser"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">N. Seriale *</Label>
                    <Input data-testid="disp-serial" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="mt-1 rounded-sm font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Modello *</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5 mb-2" data-testid="disp-model-chips">
                      {POPULAR_MODELS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          data-testid={`disp-model-chip-${m.replace(/\s+/g, "-")}`}
                          onClick={() => setForm({ ...form, model: m })}
                          className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${
                            form.model === m
                              ? "bg-brand text-white border-brand"
                              : "bg-white text-zinc-700 border-zinc-200 hover:border-brand hover:text-brand"
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                    <Input
                      data-testid="disp-model"
                      value={form.model}
                      onChange={(e) => setForm({ ...form, model: e.target.value })}
                      className="rounded-sm"
                      placeholder="es. Quattro SELF 110"
                      list="wineemotion-models"
                      autoComplete="off"
                    />
                    <datalist id="wineemotion-models">
                      {WINEEMOTION_MODELS.map((m) => (
                        <option key={m} value={m} />
                      ))}
                    </datalist>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Cliente</Label>
                  <Select value={form.customer_id || "none"} onValueChange={(v) => setForm({ ...form, customer_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="disp-customer" className="mt-1 rounded-sm">
                      <SelectValue placeholder="Seleziona cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Nessuno —</SelectItem>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Data installazione</Label>
                    <Input data-testid="disp-install-date" type="date" value={form.install_date || ""} onChange={(e) => setForm({ ...form, install_date: e.target.value })} className="mt-1 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Stato</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="disp-status" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="attivo">Attivo</SelectItem>
                        <SelectItem value="in_manutenzione">In manutenzione</SelectItem>
                        <SelectItem value="dismesso">Dismesso</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Cliente finale</Label>
                  <Input data-testid="disp-end-customer-name" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 rounded-sm" placeholder="es. Osteria Roma di Mario Rossi" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Indirizzo</Label>
                  <Input data-testid="disp-end-customer-address" value={form.order_number || ""} onChange={(e) => setForm({ ...form, order_number: e.target.value })} className="mt-1 rounded-sm" placeholder="es. Via Roma 12, Milano" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
                    <Input data-testid="disp-end-customer-email" type="email" value={form.end_customer_email || ""} onChange={(e) => setForm({ ...form, end_customer_email: e.target.value })} className="mt-1 rounded-sm" placeholder="cliente@example.com" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Telefono</Label>
                    <Input data-testid="disp-end-customer-phone" value={form.end_customer_phone || ""} onChange={(e) => setForm({ ...form, end_customer_phone: e.target.value })} className="mt-1 rounded-sm" placeholder="+39 ..." />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Note</Label>
                  <Textarea data-testid="disp-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annulla</Button>
                <Button data-testid="disp-save" className="bg-brand hover:bg-brand-dark text-white rounded-sm" onClick={save}>Salva</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
          <Input
            data-testid="dispenser-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca per n. matricola, cliente finale, indirizzo, email, telefono..."
            className="pl-9 h-9 rounded-sm text-sm"
          />
        </div>
        {searchQuery && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={() => setSearchQuery("")}
            data-testid="dispenser-search-clear"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Pulisci
          </Button>
        )}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="dispenser-sort-by" className="h-9 rounded-sm w-44 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="install_date">Ordina: data installazione</SelectItem>
            <SelectItem value="created_at">Ordina: data creazione</SelectItem>
            <SelectItem value="serial_number">Ordina: seriale</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm h-9 px-2"
          onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
          data-testid="dispenser-sort-dir"
          title={sortDir === "asc" ? "Crescente" : "Decrescente"}
        >
          {sortDir === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
        </Button>
        <div className="text-xs text-zinc-500 ml-auto" data-testid="dispenser-count">
          {filteredItems.length}{filteredItems.length !== items.length ? ` / ${items.length}` : ""} dispenser
        </div>
      </div>

      <Card className="rounded-sm border-zinc-200 p-0 overflow-hidden bg-white">
        {expiring && (
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-amber-200 bg-amber-50" data-testid="expiring-filter-banner">
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-4 w-4 text-amber-800" />
              <div className="text-sm text-amber-950">
                Filtro attivo: <b>garanzia in scadenza entro {expiring} giorni</b> — <b>{items.length}</b> dispenser attivi
              </div>
            </div>
            <Button
              data-testid="expiring-filter-clear"
              variant="outline"
              size="sm"
              className="rounded-sm border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
              onClick={() => { const p = new URLSearchParams(searchParams); p.delete("expiring"); setSearchParams(p); }}
            >
              <X className="h-3.5 w-3.5 mr-1" /> Rimuovi filtro
            </Button>
          </div>
        )}
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-medium">N. Seriale</th>
              <th className="text-left px-5 py-3 font-medium">Modello</th>
              <th className="text-left px-5 py-3 font-medium">Cliente</th>
              <th className="text-left px-5 py-3 font-medium">Installazione</th>
              <th className="text-left px-5 py-3 font-medium">Stato</th>
              <th className="text-left px-5 py-3 font-medium">Garanzia</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 && (
              <tr>
                <td colSpan={7} className="p-10 text-center text-zinc-500">
                  {items.length === 0
                    ? "Nessun dispenser ancora. Aggiungine uno per iniziare."
                    : `Nessun dispenser trovato per "${searchQuery}".`}
                </td>
              </tr>
            )}
            {filteredItems.map((d) => (
              <tr key={d.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`disp-row-${d.id}`}>
                <td className="px-5 py-3 font-mono text-zinc-900">{d.serial_number}</td>
                <td className="px-5 py-3 text-zinc-700">{d.model}</td>
                <td className="px-5 py-3 text-zinc-700">{d.customer_name || "—"}</td>
                <td className="px-5 py-3 text-zinc-700">{d.install_date || "—"}</td>
                <td className="px-5 py-3">
                  <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${statusMap[d.status] || ""}`}>
                    {d.status?.replace("_", " ")}
                  </Badge>
                </td>
                <td className="px-5 py-3">
                  {(() => {
                    const w = d.warranty;
                    if (w) {
                      // Prefer server-computed warranty (uniform with dashboard KPI)
                      const days = w.days_left;
                      const soon = w.in_warranty && days <= 60 && days >= 0;
                      const cls = !w.in_warranty
                        ? "bg-red-50 text-red-800 border-red-200"
                        : soon
                          ? "bg-amber-50 text-amber-900 border-amber-200"
                          : "bg-emerald-50 text-emerald-800 border-emerald-200";
                      const label = !w.in_warranty ? "FUORI" : soon ? `IN SCAD · ${days}gg` : "IN GAR.";
                      const expDate = new Date(w.expiry_date).toLocaleDateString("it-IT");
                      return <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${cls}`}>{label} · {expDate}</Badge>;
                    }
                    // Fallback (very old records without production date)
                    return <span className="text-zinc-400 text-xs">—</span>;
                  })()}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <Link to={`/dispenser/${d.serial_number}/history`} data-testid={`disp-history-${d.id}`}>
                      <Button variant="outline" size="sm" className="rounded-sm" title="Storia interventi">
                        <History className="h-3.5 w-3.5" />
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" className="rounded-sm" data-testid={`disp-qr-${d.id}`}
                      onClick={() => setQrFor(d)} title="QR code cliente">
                      <QrCode className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-sm" data-testid={`disp-edit-${d.id}`}
                      onClick={() => { setEditing(d); setForm({ ...empty, ...d, customer_id: d.customer_id || "" }); setOpen(true); }}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>

      <Dialog open={!!qrFor} onOpenChange={(v) => !v && setQrFor(null)}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">QR code cliente</DialogTitle>
          </DialogHeader>
          {qrFor && (
            <div className="text-center py-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{qrFor.serial_number}</div>
              <div className="font-display text-xl font-bold text-zinc-950 mt-1">{qrFor.model}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <div className="border border-zinc-200 p-4 rounded-sm bg-white text-center" data-testid="qr-block-assistenza">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-brand font-semibold mb-3">Assistenza</div>
                  <div className="inline-block">
                    <QRCodeSVG id="qr-svg-assistenza" value={qrUrl} size={180} level="M" />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">Il cliente scansiona per aprire un ticket collegato al seriale.</p>
                  <div className="text-[10px] text-zinc-500 font-mono break-all mt-2">{qrUrl}</div>
                </div>
                <div className="border border-zinc-200 p-4 rounded-sm bg-white text-center" data-testid="qr-block-garanzia">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-brand font-semibold mb-3">Registrazione Garanzia · Generico</div>
                  <div className="inline-block">
                    <QRCodeSVG id="qr-svg-garanzia" value={warrantyUrl} size={180} level="M" />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">QR generico per tutti i dispenser. Il cliente inserirà (o scansionerà) il seriale sulla pagina di registrazione. Puoi stampare questo QR in blocchi e usarlo su qualsiasi dispenser.</p>
                  <div className="text-[10px] text-zinc-500 font-mono break-all mt-2">{warrantyUrl}</div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setQrFor(null)}>Chiudi</Button>
            <Button data-testid="qr-print" onClick={printQR} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
              <Printer className="h-4 w-4 mr-2" /> Stampa entrambi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
